import axios from 'axios';
import authHeader from '@/services/auth-header';

// API URL - hybridné riešenie pre development aj production
const API_URL = process.env.VUE_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://intranet-backend.optikamania.sk/api'
    : 'http://localhost:5001/api');

/**
 * Service pre hlásenia smerom k VÝVOJÁROM systému.
 *
 * POZOR: Nemiešať s /api/feedback (nápady a podnety pre vedenie Optiky MANIA).
 * Tento service obsluhuje plávajúci widget a admin stránku Feedback.
 */
class SystemFeedbackService {
  /**
   * Získa všetky hlásenia (len admin/vývojár)
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  getAll() {
    return axios.get(`${API_URL}/system-feedback`, { headers: authHeader() });
  }

  /**
   * Získa vlastné hlásenia prihláseného používateľa
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  getMine() {
    return axios.get(`${API_URL}/system-feedback/moje`, { headers: authHeader() });
  }

  /**
   * Získa detail hlásenia podľa ID
   * @param {number} id - ID hlásenia
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  getById(id) {
    return axios.get(`${API_URL}/system-feedback/${id}`, { headers: authHeader() });
  }

  /**
   * Odošle nové hlásenie vrátane screenshotov
   * @param {Object} data - Dáta hlásenia (nadpis, obsah, typ, stranka)
   * @param {Array} files - Obrázky na upload (max 3)
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  create(data, files = []) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });

    files.forEach(file => formData.append('prilohy', file));

    // Content-Type nenastavujeme manuálne - axios doplní boundary pre multipart
    return axios.post(`${API_URL}/system-feedback`, formData, { headers: authHeader() });
  }

  /**
   * Triáž hlásenia - stav, priorita, odpoveď, riešiteľ (len admin/vývojár)
   * @param {number} id - ID hlásenia
   * @param {Object} data - Dáta na aktualizáciu
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  update(id, data) {
    return axios.put(`${API_URL}/system-feedback/${id}`, data, { headers: authHeader() });
  }

  /**
   * Vymaže hlásenie (len admin/vývojár)
   * @param {number} id - ID hlásenia
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  delete(id) {
    return axios.delete(`${API_URL}/system-feedback/${id}`, { headers: authHeader() });
  }

  /**
   * Získa počet nových hlásení (pre badge v admin menu)
   * @returns {Promise} Promise s odpoveďou zo servera
   */
  getNewCount() {
    return axios.get(`${API_URL}/system-feedback/count/nove`, { headers: authHeader() });
  }

  /**
   * Vráti verejnú URL prílohy pre zobrazenie v prehliadači
   * @param {string} filename - Názov súboru na serveri
   * @returns {string} URL prílohy
   */
  getAttachmentUrl(filename) {
    return `${API_URL.replace(/\/api\/?$/, '')}/uploads/system-feedback/${filename}`;
  }

  /**
   * Bezpečne rozparsuje JSON pole príloh z databázy
   * @param {string|Array} prilohy - Prílohy z databázy
   * @returns {Array} Pole príloh
   */
  parseAttachments(prilohy) {
    if (!prilohy) return [];
    if (Array.isArray(prilohy)) return prilohy;

    try {
      const parsed = JSON.parse(prilohy);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Chyba pri parsovaní príloh:', error);
      return [];
    }
  }
}

export default new SystemFeedbackService();
